import type { ThemeId } from '../config/themes';

export type { ThemeId } from '../config/themes';

export type Priority = 'low' | 'medium' | 'high';
export type TaskStatus = 'pending' | 'in-progress' | 'completed';
export type Recurrence = 'none' | 'daily' | 'weekly' | 'monthly';
export type RecordKind = 'task' | 'event' | 'note' | 'shopping';
export type ContentStore = 'tasks' | 'events' | 'notes' | 'shoppingItems';

export type ChecklistItem = { id: string; text: string; completed: boolean };

type BaseRecord = {
  id: string;
  categoryId: string | null;
  favorite: boolean;
  createdAt: string;
  updatedAt: string;
};

export type Task = BaseRecord & {
  title: string;
  description: string;
  checklist: ChecklistItem[];
  priority: Priority;
  status: TaskStatus;
  dueDate: string | null;
  dueTime: string | null;
  recurrence: Recurrence;
  reminder: string | null;
  completedAt: string | null;
};

export type TaskOccurrence = {
  id: string;
  taskId: string;
  occurrenceDate: string;
  completed: boolean;
  completedAt: string | null;
  updatedAt: string;
};

export type PlannerEvent = BaseRecord & {
  title: string;
  description: string;
  checklist: ChecklistItem[];
  date: string;
  startTime: string | null;
  endTime: string | null;
  location: string;
  recurrence: Recurrence;
  reminder: string | null;
};

export type Note = BaseRecord & {
  title: string;
  content: string;
  checklist: ChecklistItem[];
  pinned: boolean;
};

export type ShoppingItem = BaseRecord & {
  name: string;
  quantity: string;
  store: string;
  priority: Priority;
  notes: string;
  completed: boolean;
};

export type Category = {
  id: string;
  name: string;
  normalizedName: string;
  color: string;
  icon: string | null;
  kind: 'default' | 'custom';
};

export type Snapshot = {
  id: string;
  createdAt: string;
  appVersion: string;
  schemaVersion: 2;
  counts: BackupCounts;
  payload: BackupPayload;
};

export type Settings = {
  schemaVersion: 4;
  themeId: ThemeId;
  weekStartsOn: 0 | 1;
  timeFormat: 12 | 24;
};

export type PlannerData = {
  tasks: Task[];
  taskOccurrences: TaskOccurrence[];
  events: PlannerEvent[];
  notes: Note[];
  shoppingItems: ShoppingItem[];
  categories: Category[];
};

export type BackupCounts = {
  tasks: number;
  taskOccurrences: number;
  events: number;
  notes: number;
  shoppingItems: number;
  categories: number;
};

export type BackupPayload = PlannerData & {
  settings: Settings;
};

export type BackupFileV2 = BackupPayload & {
  signature: 'spooky-plans-backup';
  schemaVersion: 2;
  appVersion: string;
  exportedAt: string;
  counts: BackupCounts;
};

export type PlannerRecord = Task | PlannerEvent | Note | ShoppingItem;

export type SearchResult = {
  id: string;
  kind: RecordKind;
  title: string;
  excerpt: string;
  updatedAt: string;
};

export const createLocalId = (prefix: string) => `${prefix}-${crypto.randomUUID()}`;

export const normalizeCategoryName = (name: string) =>
  name.trim().toLocaleLowerCase('en').normalize('NFKC');
